import type { CsrfFetch } from '../api';
import type { Cart } from './types';

// Normalized results so callers don't repeat status-code branching.
// 'gone' covers 404/410 (cart no longer exists server-side); 'empty' is
// only returned by fetchCart (204 No Content).
export type CartFetchResult =
    | { kind: 'ok'; cart: Cart }
    | { kind: 'empty' }
    | { kind: 'error' };

export type CartMutationResult =
    | { kind: 'ok'; cart: Cart }
    | { kind: 'gone' }
    | { kind: 'error' };

// POST also reports how many units Shopify actually added so the UI can
// detect partial fulfillment (stock cap) without recomputing deltas off a
// possibly-stale prior cart.
export type AddCartLineResult =
    | { kind: 'ok'; cart: Cart; addedQuantity: number }
    | { kind: 'gone' }
    | { kind: 'error' };

async function handleMutationResponse(response: Response): Promise<CartMutationResult> {
    if (response.status === 404 || response.status === 410) {
        return { kind: 'gone' };
    }
    if (!response.ok) {
        return { kind: 'error' };
    }
    const cart = (await response.json()) as Cart;
    return { kind: 'ok', cart };
}

async function handleAddResponse(response: Response): Promise<AddCartLineResult> {
    if (response.status === 404 || response.status === 410) {
        return { kind: 'gone' };
    }
    if (!response.ok) {
        return { kind: 'error' };
    }
    const body = (await response.json()) as { cart: Cart; added_quantity: number };
    return { kind: 'ok', cart: body.cart, addedQuantity: body.added_quantity };
}

export async function fetchCart(csrfFetch: CsrfFetch): Promise<CartFetchResult> {
    try {
        const response = await csrfFetch('/cart/');
        if (response.status === 204) {
            return { kind: 'empty' };
        }
        if (!response.ok) {
            return { kind: 'error' };
        }
        const cart = (await response.json()) as Cart;
        return { kind: 'ok', cart };
    } catch {
        return { kind: 'error' };
    }
}

export interface PostCartLineBody {
    variant_id: number;
    quantity: number;
    shop_id: number;
}

export async function postCartLine(
    csrfFetch: CsrfFetch,
    body: PostCartLineBody,
): Promise<AddCartLineResult> {
    try {
        const response = await csrfFetch('/cart/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return handleAddResponse(response);
    } catch {
        return { kind: 'error' };
    }
}

export interface PutCartLineBody {
    line_id: string;
    quantity: number;
}

export async function putCartLine(
    csrfFetch: CsrfFetch,
    body: PutCartLineBody,
): Promise<CartMutationResult> {
    try {
        const response = await csrfFetch('/cart/', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return handleMutationResponse(response);
    } catch {
        return { kind: 'error' };
    }
}

export interface DeleteCartLineBody {
    line_id: string;
}

export async function deleteCartLine(
    csrfFetch: CsrfFetch,
    body: DeleteCartLineBody,
): Promise<CartMutationResult> {
    try {
        const response = await csrfFetch('/cart/', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return handleMutationResponse(response);
    } catch {
        return { kind: 'error' };
    }
}
